import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { getPriorityBadgeColor } from './dashboardUtils';

const PriorityDropdown = ({ 
  currentPriority, 
  onPriorityChange, 
  projectId, 
  size = 'sm',
  isUpdating = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const priorityOptions = [
    { value: 'critical', label: 'Critical', icon: '🔴' },
    { value: 'high', label: 'High', icon: '🟠' },
    { value: 'medium', label: 'Medium', icon: '🔵' },
    { value: 'low', label: 'Low', icon: '⚪' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handlePrioritySelect = async (newPriority) => {
    if (newPriority === currentPriority || isUpdating) return;
    
    setIsOpen(false);
    try {
      await onPriorityChange(projectId, newPriority);
    } catch (error) {
      console.error('Failed to update priority:', error);
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUpdating) {
      setIsOpen(!isOpen);
    }
  };

  const buttonClasses = size === 'lg' 
    ? 'px-3 py-1 text-sm'
    : 'px-1.5 py-0.5 text-xs';

  const dropdownClasses = size === 'lg'
    ? 'py-1 text-sm min-w-[130px]'
    : 'py-1 text-xs min-w-[110px]';

  const displayText = size === 'lg' 
    ? `${currentPriority} priority` 
    : currentPriority;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        disabled={isUpdating}
        className={`
          ${buttonClasses} 
          ${getPriorityBadgeColor(currentPriority)} 
          border rounded font-medium inline-flex items-center space-x-1
          hover:brightness-110 transition-all duration-150
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-blue-500/50
          ${size === 'lg' ? 'rounded-full' : 'rounded'}
        `}
      >
        <span>{displayText}</span>
        {!isUpdating && (
          <ChevronDown 
            className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          />
        )}
        {isUpdating && (
          <div className="w-3 h-3">
            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </button>

      {isOpen && (
        <div className={`
          absolute z-50 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl
          backdrop-blur-sm transform transition-all duration-150 origin-top
          opacity-100 scale-100
          ${dropdownClasses}
          ${size === 'lg' ? 'top-full' : 'top-full'}
        `}>
          {priorityOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handlePrioritySelect(option.value)}
              className={`
                w-full px-3 py-2 text-left hover:bg-slate-700/50 transition-colors
                flex items-center space-x-2 first:rounded-t-lg last:rounded-b-lg
                ${option.value === currentPriority ? 'bg-slate-700/30' : ''}
              `}
            >
              <div className={`w-2 h-2 rounded-full ${getPriorityBadgeColor(option.value).split(' ')[0]}`} />
              <span className="text-slate-200">{option.label}</span>
              {option.value === currentPriority && (
                <span className="ml-auto text-blue-400 text-xs">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PriorityDropdown;